import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Zap, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatMoney } from '@/lib/api';

export default function AdminFlashSales() {
    const [items, setItems] = useState([]);
    const [ads, setAds] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ ad_id: '', discount_pct: 20, slot: 'flash', starts_at: '', ends_at: '', max_stock: '' });

    const load = async () => {
        const [fs, list] = await Promise.all([api.get('/admin/flash-sales'), api.get('/admin/ads')]);
        setItems(fs.data || []);
        setAds(list.data?.items || list.data || []);
    };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!form.ad_id) return toast.error('Selecciona un anuncio');
        if (!form.starts_at || !form.ends_at) return toast.error('Fechas requeridas');
        try {
            await api.post('/admin/flash-sales', { ...form, discount_pct: parseFloat(form.discount_pct), max_stock: form.max_stock ? parseInt(form.max_stock) : null });
            toast.success('Oferta relámpago creada');
            setOpen(false); setForm({ ad_id: '', discount_pct: 20, slot: 'flash', starts_at: '', ends_at: '', max_stock: '' });
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    const remove = async (id) => {
        if (!window.confirm('¿Eliminar oferta?')) return;
        try { await api.delete(`/admin/flash-sales/${id}`); load(); } catch (e) { toast.error('Error'); }
    };

    const toggle = async (fs) => {
        try { await api.put(`/admin/flash-sales/${fs.id}`, { active: !fs.active }); load(); } catch (e) { toast.error('Error'); }
    };

    return (
        <div className="space-y-4" data-testid="admin-flash-sales-page">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Zap className="h-6 w-6 text-red-600" /><h1 className="text-2xl font-semibold">Ofertas relámpago</h1></div>
                <Button onClick={() => setOpen(true)} className="bg-red-600 hover:bg-red-700" data-testid="flash-new"><Plus className="h-4 w-4 mr-1" />Nueva</Button>
            </div>

            <div className="space-y-3">
                {items.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">Sin ofertas configuradas</div>}
                {items.map(fs => (
                    <Card key={fs.id}>
                        <CardContent className="p-4 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                {fs.ad?.media?.[0] && <img src={fs.ad.media[0].url} alt="" className="h-14 w-14 rounded object-cover" />}
                                <div>
                                    <div className="font-medium">{fs.ad?.title}</div>
                                    <div className="text-xs text-muted-foreground">-{fs.discount_pct}% • {fs.slot} • {fs.starts_at?.slice(0,16)} → {fs.ends_at?.slice(0,16)}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge className={fs.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'} onClick={() => toggle(fs)} data-testid={`flash-toggle-${fs.id}`}>{fs.active ? 'Activa' : 'Inactiva'}</Badge>
                                <Button size="icon" variant="ghost" onClick={() => remove(fs.id)} data-testid={`flash-delete-${fs.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle>Nueva oferta relámpago</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label>Anuncio</Label>
                            <Select value={form.ad_id} onValueChange={v => setForm({...form, ad_id: v})}>
                                <SelectTrigger data-testid="flash-ad-select"><SelectValue placeholder="Elige anuncio" /></SelectTrigger>
                                <SelectContent className="max-h-64">
                                    {ads.slice(0, 100).map(a => <SelectItem key={a.id} value={a.id}>{a.title} — {formatMoney(a.price)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div><Label>Descuento (%)</Label><Input type="number" min="1" max="90" value={form.discount_pct} onChange={e => setForm({...form, discount_pct: e.target.value})} data-testid="flash-discount" /></div>
                        <div>
                            <Label>Slot</Label>
                            <Select value={form.slot} onValueChange={v => setForm({...form, slot: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="flash">Flash</SelectItem><SelectItem value="deal_of_day">Oferta del día</SelectItem><SelectItem value="weekend">Fin de semana</SelectItem></SelectContent></Select>
                        </div>
                        <div><Label>Inicio</Label><Input type="datetime-local" value={form.starts_at} onChange={e => setForm({...form, starts_at: e.target.value})} data-testid="flash-starts" /></div>
                        <div><Label>Fin</Label><Input type="datetime-local" value={form.ends_at} onChange={e => setForm({...form, ends_at: e.target.value})} data-testid="flash-ends" /></div>
                        <div><Label>Stock máx. (opcional)</Label><Input type="number" value={form.max_stock} onChange={e => setForm({...form, max_stock: e.target.value})} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} className="bg-red-600 hover:bg-red-700" data-testid="flash-submit">Crear</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
